import { useQuery } from '@apollo/client';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useBranchScope } from '@/modules/auth/useAuth';
import { CrudTable, type CrudColumn } from '@/shared/crud/CrudTable';
import { useCrudList } from '@/shared/crud/useCrudList';
import { INVENTORY_MOVEMENT_KIND_FROM_GQL } from '@/shared/graphql/enums';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';

import { INVENTORY_MOVEMENTS_QUERY } from './inventory.gql';

interface Movement {
  id: string;
  branch_id: string;
  product_id: string;
  kind: string;
  quantity_delta: string;
  unit_cost_cents: string | null;
  reference_kind: string | null;
  reference_id: string | null;
  reason: string | null;
  created_at: string;
}

interface MovementsResponse {
  inventoryMovements: {
    items: Movement[];
    page: { total: number; offset: number; limit: number; has_more: boolean };
  };
}

export function MovementsHistoryPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const list = useCrudList({ limit: 50 });
  const branch_id = useBranchScope();

  const { data, loading } = useQuery<MovementsResponse>(INVENTORY_MOVEMENTS_QUERY, {
    variables: { offset: list.offset, limit: list.limit, branch_id },
  });
  const rows = (data?.inventoryMovements.items ?? []).map((row) => ({
    ...row,
    kind:
      INVENTORY_MOVEMENT_KIND_FROM_GQL[
        row.kind as keyof typeof INVENTORY_MOVEMENT_KIND_FROM_GQL
      ] ?? row.kind,
  }));

  const columns: CrudColumn<Movement>[] = [
    {
      key: 'created_at',
      header_key: 'inventory.created_at',
      width: 'w-44',
      render: (r) => new Date(r.created_at).toLocaleString('es-MX'),
    },
    {
      key: 'kind',
      header_key: 'inventory.movement_kind',
      width: 'w-44',
      render: (r) => <Badge variant="outline">{t(`inventory.kind_${r.kind}`, r.kind)}</Badge>,
    },
    {
      key: 'qty',
      header_key: 'inventory.quantity_delta',
      align: 'right',
      width: 'w-28',
      render: (r) => {
        const qty = Number(r.quantity_delta);
        const sign_class = qty > 0 ? 'text-emerald-600' : qty < 0 ? 'text-destructive' : '';
        return <span className={`font-mono ${sign_class}`}>{r.quantity_delta}</span>;
      },
    },
    { key: 'reason', header_key: 'inventory.reason', render: (r) => r.reason ?? '—' },
    {
      key: 'ref',
      header_key: 'inventory.reference',
      render: (r) =>
        r.reference_kind ? (
          <span className="font-mono text-xs text-muted-foreground">
            {r.reference_kind}/{r.reference_id?.slice(0, 8) ?? '—'}
          </span>
        ) : (
          '—'
        ),
    },
  ];

  return (
    <div className="space-y-4">
      <Button type="button" variant="ghost" size="sm" onClick={() => navigate('/app/inventory')}>
        <ArrowLeft className="h-4 w-4" aria-hidden />
        <span>{t('inventory.back_to_list')}</span>
      </Button>

      <header>
        <h2 className="text-2xl font-semibold tracking-tight">{t('inventory.history_title')}</h2>
        <p className="text-sm text-muted-foreground">{t('inventory.history_subtitle')}</p>
      </header>

      <CrudTable<Movement>
        rows={rows}
        columns={columns}
        loading={loading}
        page={
          data?.inventoryMovements.page ?? {
            total: 0,
            offset: 0,
            limit: list.limit,
            has_more: false,
          }
        }
        onPageChange={list.setOffset}
        rowKey={(r) => r.id}
        empty_label_key="inventory.history_empty"
      />
    </div>
  );
}
